import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotesIcon from '@mui/icons-material/Notes';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ChecklistIcon from '@mui/icons-material/Checklist';
import StarIcon from '@mui/icons-material/Star';

/**
 * Inspector Details Modal Component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the modal is open
 * @param {function} props.onClose - Function to call when closing the modal
 * @param {Object} props.inspectorDetails - Object containing inspector details
 * @param {string} props.inspectorDetails.name - Inspector's name
 * @param {string} props.inspectorDetails.mobileNumber - Inspector's mobile number
 * @param {string} props.inspectorDetails.timeSlot - Inspector's time slot
 * @param {string} props.inspectorDetails.status - Inspection status
 * @param {string} props.inspectorDetails.inspectionNote - Inspection note/reason
 * @param {Array} props.inspectorDetails.Documents - Supporting documents array with DocumentPath and DocumentUrl
 * @param {Array} props.inspectorDetails.inspectionCheckList - Inspection checklist array
 */
const InspectorDetailsModal = ({ open, onClose, inspectorDetails }) => {
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [currentDocument, setCurrentDocument] = React.useState(null);

  // Check if status requires showing inspection details
  const showInspectionDetails = inspectorDetails?.status &&
    ['Pass', 'Fail', 'Not Completed'].includes(inspectorDetails.status);

  // Helper function to extract filename from path
  const getFileName = (path) => {
    if (!path) return 'Document';
    const parts = path.split('/');
    return parts[parts.length - 1] || 'Document';
  };

  // Helper function to determine if file is an image
  const isImageFile = (path) => {
    if (!path) return false;
    const extension = path.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension);
  };

  // Helper function to determine if file is a PDF
  const isPdfFile = (path) => {
    if (!path) return false;
    const extension = path.toLowerCase().split('.').pop();
    return extension === 'pdf';
  };

  // Helper function to get file type icon
  const getFileIcon = (path) => {
    if (!path) return <InsertDriveFileIcon />;
    const extension = path.toLowerCase().split('.').pop();

    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
      return <ImageIcon />;
    } else if (extension === 'pdf') {
      return <PictureAsPdfIcon />;
    } else {
      return <InsertDriveFileIcon />;
    }
  };

  // Handle document view
  const handleDocumentView = (document) => {
    setCurrentDocument(document);
    setPreviewOpen(true);
  };

  // Handle document download
  const handleDocumentDownload = async (document) => {
    try {
      const fileName = getFileName(document.DocumentPath || document.DocumentUrl);

      // Create a temporary anchor element for download
      const link = document.createElement('a');
      link.href = document.DocumentUrl;
      link.download = fileName;
      link.target = '_blank';

      // For same-origin URLs, we can force download
      if (document.DocumentUrl.startsWith(window.location.origin)) {
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // For cross-origin URLs, open in new tab
        window.open(document.DocumentUrl, '_blank');
      }
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab
      window.open(document.DocumentUrl, '_blank');
    }
  };

  // Close preview modal
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setCurrentDocument(null);
  };

  // Get checklist summary
  const getChecklistSummary = () => {
    if (!inspectorDetails?.inspectionCheckList || inspectorDetails.inspectionCheckList.length === 0) {
      return { total: 0, checked: 0 };
    }

    const total = inspectorDetails.inspectionCheckList.length;
    const checked = inspectorDetails.inspectionCheckList.filter(item => item.Checked).length;
    return { total, checked };
  };

  const checklistSummary = getChecklistSummary();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: '95%', sm: '90%', md: '85%', lg: '80%', xl: '75%' },
          maxHeight: { xs: '95vh', sm: '90vh' },
          m: { xs: 1, sm: 2 }
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: 'primary.main',
        color: 'white',
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
        py: { xs: 1.5, sm: 2 }
      }}>
        Inspector Details
      </DialogTitle>
      <DialogContent sx={{ mt: 2, p: { xs: 1, sm: 2, md: 3 } }}>
        <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
          {/* First Column - Inspector Details */}
          <Grid item xs={12} lg={showInspectionDetails ? 5 : 12} xl={showInspectionDetails ? 4 : 12}>
            <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, height: 'fit-content' }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                  mb: 2,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }}
              >
                Inspector Information
              </Typography>
              <List disablePadding>
                <ListItem sx={{ px: 0, pb: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.5, sm: 0 } }}>
                    <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: { sm: 120 } }}>
                      Inspector Name
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ ml: { sm: 1 }, color: 'text.secondary' }}>
                    {inspectorDetails?.name || 'Not assigned'}
                  </Typography>
                </ListItem>
                <ListItem sx={{ px: 0, pb: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.5, sm: 0 } }}>
                    <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: { sm: 120 } }}>
                      Inspector Email
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ ml: { sm: 1 }, color: 'text.secondary', wordBreak: 'break-word' }}>
                    {inspectorDetails?.email || 'Not assigned'}
                  </Typography>
                </ListItem>
                <ListItem sx={{ px: 0, pb: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.5, sm: 0 } }}>
                    <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: { sm: 120 } }}>
                      Mobile Number
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ ml: { sm: 1 }, color: 'text.secondary' }}>
                    {inspectorDetails?.mobileNumber || 'Not available'}
                  </Typography>
                </ListItem>
                <ListItem sx={{ px: 0, pb: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.5, sm: 0 } }}>
                    <EventIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: { sm: 120 } }}>
                      Inspection Date
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ ml: { sm: 1 }, color: 'text.secondary' }}>
                    {inspectorDetails?.inspectionDate || 'Not scheduled'}
                  </Typography>
                </ListItem>
                <ListItem sx={{ px: 0, pb: 0, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.5, sm: 0 } }}>
                    <AccessTimeIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: { sm: 120 } }}>
                      Time Slot
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ ml: { sm: 1 }, color: 'text.secondary' }}>
                    {inspectorDetails?.timeSlot?.StartTime + ' - ' + inspectorDetails?.timeSlot?.EndTime || 'Not scheduled'}
                  </Typography>
                </ListItem>
              </List>
            </Paper>
          </Grid>

          {/* Second Column - Inspection Status and Documents */}
          {showInspectionDetails && (
            <Grid item xs={12} lg={7} xl={8}>
              <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, height: 'fit-content' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: 'primary.main',
                    fontWeight: 'bold',
                    mb: 2,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}
                >
                  Inspection {inspectorDetails.status === 'Pass' ? 'Completion' :
                    inspectorDetails.status === 'Fail' ? 'Failure' :
                      'Incomplete'} Details
                </Typography>

                {/* Status Chip */}
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`Status: ${inspectorDetails.status}`}
                    color={inspectorDetails.status === 'Pass' ? 'success' :
                      inspectorDetails.status === 'Fail' ? 'error' : 'warning'}
                    variant="filled"
                    size="medium"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  />
                </Box>

                {/* Inspection Note/Reason */}
                {inspectorDetails.inspectionNote && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <NotesIcon sx={{ mr: 1, color: 'primary.main', mt: 0.5 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {inspectorDetails.status === 'Pass' ? 'Completion Notes' :
                            inspectorDetails.status === 'Fail' ? 'Failure Reason' :
                              'Reason for Incomplete'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}
                        >
                          {inspectorDetails.inspectionNote}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Inspection Checklist */}
                {inspectorDetails.inspectionCheckList && inspectorDetails.inspectionCheckList.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        flexWrap: 'wrap',
                        gap: 1
                      }}>
                        <ChecklistIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ flexGrow: 1 }}>
                          Inspection Checklist
                        </Typography>
                        <Chip
                          label={`${checklistSummary.checked}/${checklistSummary.total} Completed`}
                          color={checklistSummary.checked === checklistSummary.total ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>

                      <Box sx={{
                        maxHeight: { xs: 150, sm: 180, md: 200 },
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: { xs: 0.5, sm: 1 }
                      }}>
                        <FormGroup>
                          {inspectorDetails.inspectionCheckList.map((item, index) => (
                            <FormControlLabel
                              key={index}
                              control={
                                <Checkbox
                                  checked={item.Checked}
                                  disabled
                                  size="small"
                                  sx={{
                                    color: item.Checked ? 'success.main' : 'grey.400',
                                    '&.Mui-checked': {
                                      color: 'success.main',
                                    },
                                  }}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      textDecoration: item.Checked ? 'none' : 'none',
                                      color: item.Checked ? 'text.primary' : 'text.secondary',
                                      fontWeight: item.Checked ? 'medium' : 'normal',
                                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                      lineHeight: 1.2
                                    }}
                                  >
                                    {item.Name}
                                  </Typography>
                                  {item.IsCustom && (
                                    <Tooltip title="Custom item">
                                      <StarIcon sx={{ fontSize: { xs: 12, sm: 14 }, color: 'warning.main' }} />
                                    </Tooltip>
                                  )}
                                </Box>
                              }
                              sx={{
                                mx: 0,
                                mb: 0.5,
                                '& .MuiFormControlLabel-label': {
                                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                }
                              }}
                            />
                          ))}
                        </FormGroup>
                      </Box>
                    </Box>
                  </>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Supporting Documents */}
                <Box>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                    <AttachFileIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ flexGrow: 1 }}>
                      Supporting Documents
                      {inspectorDetails.Documents && inspectorDetails.Documents.length > 0 &&
                        ` (${inspectorDetails.Documents.length})`}
                    </Typography>
                  </Box>

                  {inspectorDetails.Documents && inspectorDetails.Documents.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 1 } }}>
                      {inspectorDetails.Documents.map((document, index) => {
                        const fileName = document.name || getFileName(document.DocumentPath || document.DocumentUrl) || `Document ${index + 1}`;
                        const filePath = document.DocumentPath || document.DocumentUrl;

                        return (
                          <Box
                            key={document.id || index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: { xs: 0.5, sm: 1 },
                              p: { xs: 0.5, sm: 1 },
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              '&:hover': {
                                bgcolor: 'grey.50'
                              }
                            }}
                          >
                            {getFileIcon(filePath)}
                            <Typography
                              variant="body2"
                              sx={{
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                              }}
                            >
                              {fileName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 } }}>
                              <Tooltip title="View">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDocumentView(document)}
                                  sx={{ color: 'primary.main', p: { xs: 0.5, sm: 1 } }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDocumentDownload(document)}
                                  sx={{ color: 'primary.main', p: { xs: 0.5, sm: 1 } }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '1px dashed',
                      borderColor: 'grey.300'
                    }}>
                      <AttachFileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        No supporting documents available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          // size={{ xs: "medium", sm: "large" }}
          sx={{ minWidth: { xs: 100, sm: 120 } }}
        >
          Close
        </Button>
      </DialogActions>

      {/* Document/Image Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: '95vh', sm: '90vh' },
            width: { xs: '95%', sm: '90%', md: '85%' },
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 }
        }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1rem', sm: '1.25rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: '60%', sm: '70%' }
            }}
          >
            {currentDocument ? getFileName(currentDocument.DocumentPath || currentDocument.DocumentUrl) : 'Document Preview'}
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
            <Tooltip title="Download">
              <IconButton
                onClick={() => currentDocument && handleDocumentDownload(currentDocument)}
                sx={{ color: 'white', p: { xs: 0.5, sm: 1 } }}
                size="small"
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                onClick={handleClosePreview}
                sx={{ color: 'white', p: { xs: 0.5, sm: 1 } }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {currentDocument && (
            <>
              {isImageFile(currentDocument.DocumentPath || currentDocument.DocumentUrl) ? (
                // Image Preview
                <Box sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: { xs: 1, sm: 2 }
                }}>
                  <img
                    src={currentDocument.DocumentUrl}
                    alt={getFileName(currentDocument.DocumentPath || currentDocument.DocumentUrl)}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  />
                </Box>
              ) : isPdfFile(currentDocument.DocumentPath || currentDocument.DocumentUrl) ? (
                // PDF Preview
                <iframe
                  src={currentDocument.DocumentUrl}
                  title={getFileName(currentDocument.DocumentPath || currentDocument.DocumentUrl)}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                />
              ) : (
                // Other file types - show info and download option
                <Box sx={{
                  textAlign: 'center',
                  p: { xs: 2, sm: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2
                }}>
                  {getFileIcon(currentDocument.DocumentPath || currentDocument.DocumentUrl)}
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      textAlign: 'center',
                      wordBreak: 'break-word'
                    }}
                  >
                    {getFileName(currentDocument.DocumentPath || currentDocument.DocumentUrl)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textAlign: 'center',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    This file type cannot be previewed in the browser.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDocumentDownload(currentDocument)}
                  // size={{ xs: "small", sm: "medium" }}
                  >
                    Download File
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default InspectorDetailsModal;